import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch } from 'react-redux';

import { Button } from 'common-src/components/base';
import { apiRequest, HttpMethods } from 'common-src/features/rest';
import { ColorsNew } from 'common-src/styles';

import { ModalType } from 'src/components/base/ModalGroup';
import { openModal } from 'src/features/modals';
import useAlert from 'src/hooks/useAlert';

import { CognitoStatusEnum, StatusEnum } from './constants';
import styles from './Security.module.scss';

const Security = ({ portalPatientId, cognitoStatus, onStatusChange }) => {
  const dispatch = useDispatch();
  const { showAlert, AlertType } = useAlert();

  const handleSetPasswordClick = () => {
    dispatch(openModal(ModalType.SET_PASSWORD, { portalPatientId }));
  };

  const performActivate = async () => {
    try {
      const response = await apiRequest({
        endpoint: 'patientPortalBackend/activateOrDeactivate',
        method: HttpMethods.Post,
        body: { patient_id: portalPatientId, status: StatusEnum.ACTIVE },
      });
      if (response.ok) {
        showAlert(AlertType.Success, '', 'User activated successfully.');
        if (onStatusChange) onStatusChange();
      } else {
        const error = await response.json();
        showAlert(AlertType.Error, '', error.message || 'Failed to activate user.');
      }
    } catch (error) {
      showAlert(AlertType.Error, '', error.message || 'Network error while activating user.');
    }
  };

  const performDeactivate = async () => {
    try {
      const response = await apiRequest({
        endpoint: 'patientPortalBackend/activateOrDeactivate',
        method: HttpMethods.Post,
        body: { patient_id: portalPatientId, status: StatusEnum.INACTIVE, send_email: 'false' },
      });
      if (response.ok) {
        showAlert(AlertType.Success, '', 'User deactivated successfully.');
        if (onStatusChange) onStatusChange();
      } else {
        const error = await response.json();
        showAlert(AlertType.Error, '', error.message || 'Failed to deactivate user.');
      }
    } catch (error) {
      showAlert(AlertType.Error, '', error.message || 'Network error while deactivating user.');
    }
  };

  const handleActivate = () => {
    dispatch(
      openModal(ModalType.ACTIVATE_DEACTIVATE_CONFIRMATION, {
        title: 'Activate User',
        message: 'Are you sure you want to activate this user?',
        submitButtonText: 'Yes, Activate',
        onConfirm: performActivate,
      }),
    );
  };

  const handleDeactivate = () => {
    dispatch(
      openModal(ModalType.ACTIVATE_DEACTIVATE_CONFIRMATION, {
        title: 'Deactivate User',
        message: 'Are you sure you want to deactivate this user?',
        submitButtonText: 'Yes, Deactivate',
        onConfirm: performDeactivate,
      }),
    );
  };

  return (
    <div className={styles.form}>
      <div className={styles.passwordSection}>
        <div className={styles.sectionTitle}>Patient Portal Password</div>
        <Button
          id="set-patient-password-button"
          text="Set Patient Password"
          backgroundColor={ColorsNew.darkGreen}
          textColor={ColorsNew.white}
          classNames={[styles.button]}
          onClick={handleSetPasswordClick}
        />
      </div>
      <div className={styles.activateSection}>
        <div className={styles.activateTitle}>Activate / Deactivate user</div>
        <div className={styles.activateActions}>
          {cognitoStatus === undefined || cognitoStatus === null ? (
            <span>Loading...</span>
          ) : (
            <>
              <span
                title={
                  cognitoStatus === CognitoStatusEnum.ACTIVE ? 'User is already active' : undefined
                }
                style={{ display: 'inline-block' }}
              >
                <Button
                  id="activate-user-button"
                  text="Activate"
                  backgroundColor={ColorsNew.darkGreen}
                  textColor={ColorsNew.white}
                  classNames={[styles.button]}
                  onClick={handleActivate}
                  disabled={cognitoStatus === CognitoStatusEnum.ACTIVE}
                />
              </span>
              <span
                title={
                  cognitoStatus === CognitoStatusEnum.INACTIVE
                    ? 'User is already inactive'
                    : undefined
                }
                style={{ display: 'inline-block' }}
              >
                <Button
                  id="deactivate-user-button"
                  text="Deactivate"
                  backgroundColor={ColorsNew.mediumRed}
                  borderColor={ColorsNew.mediumRed}
                  textColor={ColorsNew.white}
                  classNames={[styles.button]}
                  onClick={handleDeactivate}
                  disabled={cognitoStatus === CognitoStatusEnum.INACTIVE}
                />
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

Security.propTypes = {
  portalPatientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  cognitoStatus: PropTypes.string,
  onStatusChange: PropTypes.func,
};

export default Security;
